import React, { useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaBroadcastTower,
  FaBell,
  FaBirthdayCake,
  FaCog,
  FaHistory,
  FaImage,
  FaListOl,
  FaPalette,
  FaPlayCircle,
  FaQuestionCircle,
  FaSignal,
  FaStore,
  FaTv,
  FaUpload,
} from "react-icons/fa";
import TvDashboard from "./TvDashboard";
import TvDevices from "./TvDevices";
import TvSettings from "./TvSettings";
import TvPreview from "./TvPreview";
import TvMediaLibrary from "./TvMediaLibrary";
import TvPlaylists from "./TvPlaylists";
import TvPublishing from "./TvPublishing";
import TvOutlets from "./TvOutlets";
import TvCelebrations from "./TvCelebrations";
import "./TvDisplay.css";
import { TV_DISPLAY_DEFAULT_CONFIG } from "../../services/tv-disply/tvDisplayConstants";
import {
  initializeTvDisplayConfig,
  subscribeToTvDisplayConfig,
} from "../../services/tv-disply/tvDisplayService";
import {
  subscribeToTvDevices,
  subscribeToTvPairings,
} from "../../services/tv-disply/tvDeviceService";
import { subscribeToTvMedia } from "../../services/tv-disply/tvMediaService";
import { subscribeToTvPlaylists } from "../../services/tv-disply/tvPlaylistService";
import { subscribeToPublications } from "../../services/tv-disply/tvPublishService";
import {
  TV_DEVICE_STATUSES,
  TV_PAIRING_STATUSES,
} from "../../services/tv-disply/tvDisplayConstants";

export default function TvDisplay() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [config, setConfig] = useState(TV_DISPLAY_DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [docReady, setDocReady] = useState(false);
  const [mediaRows, setMediaRows] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(true);
  const [mediaError, setMediaError] = useState("");
  const [playlistRows, setPlaylistRows] = useState([]);
  const [playlistLoading, setPlaylistLoading] = useState(true);
  const [playlistError, setPlaylistError] = useState("");
  const [deviceRows, setDeviceRows] = useState([]);
  const [deviceLoading, setDeviceLoading] = useState(true);
  const [deviceError, setDeviceError] = useState("");
  const [pairingRows, setPairingRows] = useState([]);
  const [pairingLoading, setPairingLoading] = useState(true);
  const [pairingError, setPairingError] = useState("");
  const [publicationRows, setPublicationRows] = useState([]);
  const [publicationLoading, setPublicationLoading] = useState(true);
  const [publicationError, setPublicationError] = useState("");

  useEffect(() => {
    let isActive = true;

    async function boot() {
      try {
        setLoading(true);
        await initializeTvDisplayConfig();
        if (!isActive) return;
        setDocReady(true);
        setError("");
      } catch (err) {
        if (!isActive) return;
        setError(resolveTvDisplayError(err, "Unable to initialize TV Display foundation."));
        setLoading(false);
      }
    }

    boot();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (!docReady) return undefined;

    const unsubscribe = subscribeToTvDisplayConfig(
      (nextConfig) => {
        setConfig(nextConfig);
        setLoading(false);
        setError("");
      },
      (err) => {
        setError(resolveTvDisplayError(err, "Unable to sync TV Display configuration in realtime."));
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [docReady]);

  useEffect(() => {
    if (!docReady) return undefined;

    setPublicationLoading(true);
    const unsubscribe = subscribeToPublications(
      (nextRows) => {
        setPublicationRows(nextRows);
        setPublicationLoading(false);
        setPublicationError("");
      },
      (err) => {
        setPublicationError(resolveTvDisplayError(err, "Unable to sync TV publications in realtime."));
        setPublicationLoading(false);
      }
    );

    return () => unsubscribe();
  }, [docReady]);

  useEffect(() => {
    if (!docReady) return undefined;

    setPlaylistLoading(true);
    const unsubscribe = subscribeToTvPlaylists(
      (nextRows) => {
        setPlaylistRows(nextRows);
        setPlaylistLoading(false);
        setPlaylistError("");
      },
      (err) => {
        setPlaylistError(resolveTvDisplayError(err, "Unable to sync TV Display playlists in realtime."));
        setPlaylistLoading(false);
      }
    );

    return () => unsubscribe();
  }, [docReady]);

  useEffect(() => {
    if (!docReady) return undefined;

    setMediaLoading(true);
    const unsubscribe = subscribeToTvMedia(
      (nextRows) => {
        setMediaRows(nextRows);
        setMediaLoading(false);
        setMediaError("");
      },
      (err) => {
        setMediaError(resolveTvDisplayError(err, "Unable to sync TV Display media library in realtime."));
        setMediaLoading(false);
      }
    );

    return () => unsubscribe();
  }, [docReady]);

  useEffect(() => {
    if (!docReady) return undefined;

    setDeviceLoading(true);
    const unsubscribe = subscribeToTvDevices(
      (nextRows) => {
        setDeviceRows(nextRows);
        setDeviceLoading(false);
        setDeviceError("");
      },
      (err) => {
        setDeviceError(resolveTvDisplayError(err, "Unable to sync TV device registry in realtime."));
        setDeviceLoading(false);
      }
    );

    return () => unsubscribe();
  }, [docReady]);

  useEffect(() => {
    if (!docReady) return undefined;

    setPairingLoading(true);
    const unsubscribe = subscribeToTvPairings(
      (nextRows) => {
        setPairingRows(nextRows);
        setPairingLoading(false);
        setPairingError("");
      },
      (err) => {
        setPairingError(resolveTvDisplayError(err, "Unable to sync TV pairing requests in realtime."));
        setPairingLoading(false);
      }
    );

    return () => unsubscribe();
  }, [docReady]);

  const mediaStats = useMemo(() => {
    const videos = mediaRows.filter((row) => row.type === "VIDEO").length;
    const images = mediaRows.filter((row) => row.type === "IMAGE").length;
    const active = mediaRows.filter((row) => row.enabled).length;
    return {
      total: mediaRows.length,
      videos,
      images,
      active,
      disabled: Math.max(0, mediaRows.length - active),
    };
  }, [mediaRows]);

  const playlistStats = useMemo(() => {
    const enabled = playlistRows.filter((row) => row.enabled).length;
    const published = playlistRows.filter((row) => row.lastPublishedVersion > 0).length;
    const itemCount = playlistRows.reduce((sum, row) => sum + Math.max(0, Number(row.itemCount || 0)), 0);
    return {
      total: playlistRows.length,
      enabled,
      published,
      disabled: Math.max(0, playlistRows.length - enabled),
      itemCount,
    };
  }, [playlistRows]);

  const deviceStats = useMemo(() => {
    const online = deviceRows.filter((row) => row.computedStatus === TV_DEVICE_STATUSES.ONLINE).length;
    const disabled = deviceRows.filter((row) => row.computedStatus === TV_DEVICE_STATUSES.DISABLED).length;
    const revoked = deviceRows.filter((row) => row.computedStatus === TV_DEVICE_STATUSES.REVOKED).length;
    const offline = Math.max(0, deviceRows.length - online - disabled - revoked);
    const published = deviceRows.filter((row) => row.lastPublishedVersion > 0).length;
    const updatePending = deviceRows.filter((row) => row.lastPublishedVersion > 0 && row.lastAppliedVersion != null && row.lastAppliedVersion < row.lastPublishedVersion).length;
    const upToDate = deviceRows.filter((row) => row.lastPublishedVersion > 0 && row.lastAppliedVersion != null && row.lastAppliedVersion >= row.lastPublishedVersion).length;
    const error = deviceRows.filter((row) => Boolean(row.lastSyncError)).length;
    return {
      total: deviceRows.length,
      online,
      offline,
      disabled,
      revoked,
      published,
      updatePending,
      upToDate,
      error,
    };
  }, [deviceRows]);

  const pairingStats = useMemo(() => {
    const pending = pairingRows.filter((row) => row.status === TV_PAIRING_STATUSES.REQUESTED).length;
    const expired = pairingRows.filter((row) => row.status === TV_PAIRING_STATUSES.EXPIRED).length;
    const completed = pairingRows.filter((row) => row.status === TV_PAIRING_STATUSES.COMPLETED).length;
    return {
      total: pairingRows.length,
      pending,
      expired,
      completed,
    };
  }, [pairingRows]);

  const sectionTabs = useMemo(
    () => [
      {
        id: "dashboard",
        label: "Overview",
        title: "Overview",
        description: "A clear view of content, screens, pairings and delivery health.",
        icon: <FaSignal />,
        content: "dashboard",
      },
      {
        id: "outlets",
        label: "All Outlets",
        title: "All Outlets",
        description: "Open any outlet to preview, edit and publish its TV experience.",
        icon: <FaStore />,
        content: "outlets",
      },
      {
        id: "celebrations",
        label: "Celebrations",
        title: "Celebrations",
        description: "Schedule or instantly take over every TV with a special moment.",
        icon: <FaBirthdayCake />,
        content: "celebrations",
      },
      {
        id: "media",
        label: "Media Library",
        title: "Media Library",
        description: "Upload and organise the images and videos used across every outlet.",
        icon: <FaImage />,
        content: "media",
      },
      {
        id: "playlists",
        label: "Playlists",
        title: "Playlists",
        description: "Build content schedules, arrange media and manage playlist drafts.",
        icon: <FaListOl />,
        content: "playlists",
      },
      {
        id: "presentation",
        label: "Presentation",
        title: "Presentation",
        description: "Configure orientation, transitions, media fit and promotional overlays.",
        icon: <FaPalette />,
        content: "playlists",
      },
      {
        id: "preview",
        label: "Live Preview",
        title: "Live Preview",
        description: "Review the real playlist output before sending it to connected screens.",
        icon: <FaPlayCircle />,
        content: "preview",
      },
      {
        id: "devices",
        label: "TVs",
        title: "Connected TVs",
        description: "Monitor and manage registered screens across all ApnaArtist outlets.",
        icon: <FaTv />,
        content: "devices",
      },
      {
        id: "pairing",
        label: "Pair New TV",
        title: "Pair New TV",
        description: "Verify the code shown on a new TV and approve its connection.",
        icon: <FaSignal />,
        content: "devices",
      },
      {
        id: "publishing",
        label: "Publish",
        title: "Publish Content",
        description: "Choose a ready playlist and safely deliver it to eligible TVs.",
        icon: <FaBroadcastTower />,
        content: "publishing",
      },
      {
        id: "history",
        label: "History",
        title: "Publication History",
        description: "Review immutable publication versions and device delivery results.",
        icon: <FaHistory />,
        content: "publishing",
      },
      {
        id: "settings",
        label: "Settings",
        title: "Settings",
        description: "Review the shared display configuration used by the TV network.",
        icon: <FaCog />,
        content: "settings",
      },
    ],
    []
  );

  const activeSection = sectionTabs.find((tab) => tab.id === activeTab) || sectionTabs[0];

  const activeContent = activeSection.content;
  const attentionCount = deviceStats.offline + deviceStats.updatePending + deviceStats.error + pairingStats.pending;

  return (
    <div className="tv-display-page">
      <header className="tv-display-app-header">
        <button type="button" className="tv-display-back" onClick={() => window.history.back()}>
          <FaArrowLeft />
          <span>Back to Dashboard</span>
        </button>
        <div className="tv-display-header-title">
          <h1>TV Display</h1>
          <p>Manage media, playlists and connected TV screens across all outlets</p>
        </div>
        <div className="tv-display-header-actions">
          <span className={`tv-display-health${attentionCount ? " needs-attention" : ""}`}>
            <span className="tv-display-health-dot" />
            {attentionCount ? `${attentionCount} need attention` : "All systems healthy"}
          </span>
          {pairingStats.pending ? <span className="tv-display-notification-count">{pairingStats.pending}</span> : null}
          <button type="button" className="tv-display-header-icon" aria-label="Notifications"><FaBell /></button>
          <button type="button" className="tv-display-header-icon" aria-label="Help"><FaQuestionCircle /></button>
        </div>
      </header>

      {loading ? (
        <div className="tv-display-feedback is-loading">
          Initializing the shared `tv-disply/main` configuration document and starting realtime sync.
        </div>
      ) : null}

      {error ? (
        <div className="tv-display-feedback is-error">{error}</div>
      ) : null}

      <div className="tv-display-workspace">
        <aside className="tv-display-sidebar">
          <div className="tv-display-brand">
            <span className="tv-display-brand-mark">A</span>
            <span><strong>ApnaArtist</strong><small>TV DISPLAY</small></span>
          </div>
          <nav className="tv-display-tab-list is-sidebar" aria-label="TV Display sections">
              {sectionTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`tv-display-tab-btn${activeTab === tab.id ? " is-active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span className="tv-display-tab-icon">{tab.icon}</span>
                  <span className="tv-display-tab-label">{tab.label}</span>
                  {tab.id === "pairing" && pairingStats.pending ? <span className="tv-display-nav-count">{pairingStats.pending}</span> : null}
                </button>
              ))}
          </nav>
          <div className="tv-display-sidebar-status">
            <span className="tv-display-health-dot" />
            <span><strong>{deviceStats.online} TVs online</strong><small>{deviceStats.total} registered screens</small></span>
          </div>
        </aside>

        <main className="tv-display-main">
          <section className={`tv-display-content-head${activeContent === "outlets" ? " is-outlets" : ""}${activeContent === "celebrations" ? " is-celebrations" : ""}`}>
            <div>
              <h2>{activeSection.title}</h2>
              <p>{activeSection.description}</p>
            </div>
            <div className="tv-display-content-actions">
              <button type="button" className="tv-display-action-btn" onClick={() => setActiveTab("media")}><FaUpload /> Upload Media</button>
              <button type="button" className="tv-display-action-btn is-primary" onClick={() => setActiveTab("publishing")}><FaBroadcastTower /> Publish</button>
            </div>
          </section>

          <div className="tv-display-layout">
            <section className={`tv-display-section-shell${activeContent === "celebrations" ? " is-active" : ""}`}>
              <TvCelebrations deviceRows={deviceRows} />
            </section>

            <section className={`tv-display-section-shell${activeContent === "outlets" ? " is-active" : ""}`}>
              <TvOutlets
                deviceRows={deviceRows}
                playlistRows={playlistRows}
                mediaRows={mediaRows}
                loading={deviceLoading || playlistLoading || mediaLoading}
              />
            </section>

            <section className={`tv-display-section-shell${activeContent === "dashboard" ? " is-active" : ""}`}>
          <TvDashboard
            config={config}
            loading={loading || mediaLoading || playlistLoading || deviceLoading || pairingLoading || publicationLoading}
            mediaStats={mediaStats}
            playlistStats={playlistStats}
            deviceStats={deviceStats}
            pairingStats={pairingStats}
            publicationStats={{ total: publicationRows.length }}
            deviceRows={deviceRows}
            onNavigate={setActiveTab}
          />
            </section>

            <section className={`tv-display-section-shell${activeContent === "media" ? " is-active" : ""}`}>
          <TvMediaLibrary
            mediaRows={mediaRows}
            mediaLoading={mediaLoading}
            mediaError={mediaError}
            mediaStats={mediaStats}
          />
            </section>

            <section className={`tv-display-section-shell${activeContent === "playlists" ? " is-active" : ""}`}>
          <TvPlaylists
            playlistRows={playlistRows}
            playlistLoading={playlistLoading}
            playlistError={playlistError}
            mediaRows={mediaRows}
            mediaLoading={mediaLoading}
            mediaError={mediaError}
            playlistStats={playlistStats}
          />
            </section>

            <section className={`tv-display-section-shell${activeContent === "publishing" ? " is-active" : ""}`}>
          <TvPublishing
            playlistRows={playlistRows}
            deviceRows={deviceRows}
            publicationRows={publicationRows}
          />
            </section>

            <section className={`tv-display-section-shell${activeContent === "devices" ? " is-active" : ""}`}>
          <TvDevices
            deviceRows={deviceRows}
            deviceLoading={deviceLoading}
            deviceError={deviceError}
            pairingRows={pairingRows}
            pairingLoading={pairingLoading}
            pairingError={pairingError}
            deviceStats={deviceStats}
            pairingStats={pairingStats}
          />
            </section>

            <section className={`tv-display-section-shell${activeContent === "settings" ? " is-active" : ""}`}>
          <TvSettings config={config} />
            </section>

            <section className={`tv-display-section-shell${activeContent === "preview" ? " is-active" : ""}`}>
          <TvPreview config={config} playlistRows={playlistRows} mediaRows={mediaRows} />
            </section>

            {publicationError ? <div className="tv-display-feedback is-error">{publicationError}</div> : null}
          </div>
        </main>
      </div>
    </div>
  );
}

function resolveTvDisplayError(error, fallback) {
  const code = String(error?.code || "").toLowerCase();
  if (code.includes("permission-denied")) return "You do not have permission to access TV Display data.";
  if (code.includes("unavailable")) return "Firestore is temporarily unavailable. Please try again.";
  return fallback;
}
